import { useState } from 'react';
import { Sparkles, RefreshCw, AlertTriangle, Brain, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSubscription } from '../hooks/useSubscription';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAiModel } from '../hooks/useAiModel';
import type { DashboardPnlData } from '../hooks/useDashboardPnl';
import { logError } from "../lib/logError";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface PnlDashboardInsightProps {
  pnlData: DashboardPnlData;
}

// ─────────────────────────────────────────────────────────────────────────────
// Enhanced Markdown Components
// ─────────────────────────────────────────────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any -- react-markdown component overrides use internal render prop signatures */
const markdownComponents = {
  table: ({ node, ...props }: any) => (
    <div style={{ overflowX: 'auto', margin: '1rem 0', borderRadius: 10, border: '1px solid #e5e7eb' }}>
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontSize: '0.82rem',
        }}
        {...props}
      />
    </div>
  ),
  thead: ({ node, ...props }: any) => (
    <thead
      style={{
        background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
      }}
      {...props}
    />
  ),
  th: ({ node, ...props }: any) => (
    <th
      style={{
        border: '1px solid #e5e7eb',
        padding: '8px 12px',
        fontWeight: 600,
        textAlign: 'left',
        fontSize: '0.78rem',
        color: '#5b21b6',
      }}
      {...props}
    />
  ),
  td: ({ node, ...props }: any) => (
    <td
      style={{
        border: '1px solid #e5e7eb',
        padding: '8px 12px',
        fontSize: '0.8rem',
      }}
      {...props}
    />
  ),
  h2: ({ node, ...props }: any) => (
    <h2
      style={{
        fontSize: '1rem',
        fontWeight: 700,
        color: 'var(--text-primary, #111827)',
        marginTop: '1.2rem',
        marginBottom: '0.5rem',
        paddingBottom: '4px',
        borderBottom: '2px solid #ede9fe',
      }}
      {...props}
    />
  ),
  h3: ({ node, ...props }: any) => (
    <h3
      style={{
        fontSize: '0.92rem',
        fontWeight: 600,
        color: '#5b21b6',
        marginTop: '1rem',
        marginBottom: '0.4rem',
      }}
      {...props}
    />
  ),
  strong: ({ node, ...props }: any) => (
    <strong
      style={{
        fontWeight: 600,
        color: 'var(--text-primary, #111827)',
      }}
      {...props}
    />
  ),
  ul: ({ node, ...props }: any) => (
    <ul
      style={{
        margin: '0.5rem 0',
        paddingLeft: '1.2rem',
      }}
      {...props}
    />
  ),
  li: ({ node, ...props }: any) => (
    <li
      style={{
        marginBottom: '4px',
        lineHeight: 1.6,
      }}
      {...props}
    />
  ),
  blockquote: ({ node, ...props }: any) => (
    <blockquote
      style={{
        borderLeft: '3px solid #7c3aed',
        padding: '8px 16px',
        margin: '12px 0',
        background: '#f5f3ff',
        borderRadius: '0 8px 8px 0',
        fontSize: '0.85rem',
        color: '#4c1d95',
        fontStyle: 'italic',
      }}
      {...props}
    />
  ),
  p: ({ node, ...props }: any) => (
    <p
      style={{
        margin: '0.5rem 0',
        lineHeight: 1.7,
      }}
      {...props}
    />
  ),
};
/* eslint-enable @typescript-eslint/no-explicit-any */

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function PnlDashboardInsight({ pnlData }: PnlDashboardInsightProps) {
  const [insight, setInsight] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const aiModel = useAiModel();
  const { logAiUsage, canUseAi } = useSubscription();

  const generateInsight = async () => {
    const check = canUseAi("pnl_insights");
    if (!check.allowed) {
      setError(check.reason || "AI usage not available");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const payload = {
        ai_model: aiModel,
        totalIncome: pnlData.totalIncome,
        totalCosts: pnlData.totalCosts,
        totalProfit: pnlData.totalProfit,
        totalProfitPercent: pnlData.totalProfitPercent,
        totalRetention: pnlData.totalRetention,
        projectCount: pnlData.perProjectPnl.length,
        projects: pnlData.perProjectPnl.map((p) => ({
          name: p.projectName,
          income: p.constructionIncome,
          costs: p.totalDeductions,
          profit: p.netProfit,
          profitPercent: p.profitPercent,
          materialsCost: p.materialsCost,
          wagesCost: p.wagesCost,
          otherCosts: p.otherCosts,
          materialsPercent: p.materialsPercent,
          wagesPercent: p.wagesPercent,
          retention: p.retention,
          estimateProfit: p.estimateProfit,
        })),
        costBreakdown: pnlData.costBreakdown.map((c) => ({
          name: c.name,
          value: c.value,
        })),
        monthlyTrend: pnlData.monthlyTrend.map((m) => ({
          label: m.label,
          income: m.income,
          costs: m.costs,
          profit: m.profit,
          profitPercent: m.profitPercent,
        })),
      };

      const { data, error: fnError } = await supabase.functions.invoke(
        'generate-pnl-dashboard-insights',
        { body: payload },
      );

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      setInsight(data.analysis);
      setIsCollapsed(false);

      // Log AI credit usage with token data
      if (data.usage) {
        logAiUsage("pnl_insights", data.usage);
      } else {
        logAiUsage("pnl_insights");
      }
    } catch (err) {
      logError('Failed to generate P&L dashboard insight:', err);
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setError('Connection error. The edge function may not be deployed yet.');
      } else {
        setError(
          err instanceof Error ? err.message : 'Unable to generate P&L portfolio insight.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── CTA State: No insight generated yet ───
  if (!insight && !loading && !error) {
    return (
      <div
        className="card pnl-chart-card"
        style={{
          background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #ddd6fe 100%)',
          border: '1px solid #c4b5fd',
          borderRadius: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#7c3aed',
                boxShadow: '0 2px 8px rgba(124, 58, 237, 0.15)',
              }}
            >
              <Brain size={22} />
            </div>
            <div>
              <h3
                style={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#5b21b6',
                  marginBottom: 2,
                }}
              >
                AI Financial Analyst
              </h3>
              <p style={{ fontSize: '0.8rem', color: '#6d28d9' }}>
                Get a detailed portfolio analysis with project rankings, cost structure benchmarks, risk scores, and actionable recommendations.
              </p>
            </div>
          </div>
          <button
            onClick={generateInsight}
            className="btn"
            disabled={pnlData.loading || pnlData.perProjectPnl.length === 0}
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              color: 'white',
              border: 'none',
              boxShadow: '0 2px 6px rgba(124, 58, 237, 0.3)',
              borderRadius: 10,
              padding: '10px 20px',
              fontWeight: 500,
              opacity: pnlData.loading || pnlData.perProjectPnl.length === 0 ? 0.5 : 1,
            }}
          >
            <Sparkles size={16} style={{ marginRight: 6 }} />
            Analyze Portfolio
          </button>
        </div>
      </div>
    );
  }

  // ─── Result / Loading / Error State ───
  return (
    <div className="card pnl-chart-card" style={{ borderTop: '4px solid #7c3aed', borderRadius: 16 }}>
      <div
        className="card-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Brain size={18} style={{ color: '#7c3aed' }} />
          <h3 className="card-title" style={{ margin: 0 }}>
            AI P&L Portfolio Analysis
          </h3>
          {insight && !loading && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-muted, #9ca3af)',
                display: 'flex',
                alignItems: 'center',
                padding: 4,
              }}
              title={isCollapsed ? 'Expand insight' : 'Collapse insight'}
            >
              {isCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </button>
          )}
        </div>
        <button
          onClick={generateInsight}
          disabled={loading}
          className="btn btn-ghost btn-sm"
          style={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}
        >
          {loading ? (
            <RefreshCw size={16} className="spin" />
          ) : (
            <RefreshCw size={16} />
          )}
          {loading ? 'Analyzing...' : 'Refresh'}
        </button>
      </div>

      {!isCollapsed && (
        <div style={{ lineHeight: 1.65, fontSize: '0.9rem', color: 'var(--text-primary, #374151)' }}>
          {error ? (
            <div
              style={{
                display: 'flex',
                gap: 8,
                color: '#dc2626',
                alignItems: 'center',
                padding: '12px',
                background: '#fef2f2',
                borderRadius: 10,
              }}
            >
              <AlertTriangle size={18} />
              {error}
            </div>
          ) : loading ? (
            <div
              style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}
            >
              <div className="spinner" style={{ margin: '0 auto 12px' }} />
              <p>Analyzing P&L across {pnlData.perProjectPnl.length} projects...</p>
              <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Generating detailed rankings, cost analysis, and strategic recommendations…</p>
            </div>
          ) : (
            <div className="markdown-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {insight || ''}
              </ReactMarkdown>
              <div
                style={{
                  marginTop: 16,
                  borderTop: '1px solid #e5e7eb',
                  paddingTop: 10,
                  fontSize: '0.7rem',
                  color: '#9ca3af',
                  fontStyle: 'italic',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Brain size={12} />
                AI-generated portfolio analysis. Verify all figures with individual project P&L data.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
